// components/AppInitializer.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { useDoctor } from '../contexts/DoctorContext';
import { useAuth } from '../contexts/AuthContext';
import DoctorNameInput from './DoctorNameInput';
import { initializeSampleData } from '../utils/generateSampleData';

const AppInitializer = ({ children }) => {
  const { doctorName, isInitialized } = useDoctor();
  const { isLoggedIn, loading: authLoading } = useAuth();
  const [showDoctorInput, setShowDoctorInput] = useState(false);
  const [isFirstLaunch, setIsFirstLaunch] = useState(false);

  useEffect(() => {
    if (isInitialized && !authLoading) {
      // Check if this is the first launch (no doctor name and no data)
      const hasData = localStorage.getItem('medical_platform_data');
      if (!doctorName && !hasData) {
        setIsFirstLaunch(true);
        setShowDoctorInput(true);
      }
      // Si l'utilisateur est connecté mais n'a pas de nom de médecin, ouvrir le modal
      else if (isLoggedIn && !doctorName) {
        setShowDoctorInput(true);
      }
    }
  }, [isInitialized, doctorName, isLoggedIn, authLoading]);

  const handleDoctorNameSet = () => {
    setShowDoctorInput(false);
    setIsFirstLaunch(false);
    
    // Initialize sample data on first launch
    if (isFirstLaunch) {
      initializeSampleData();
    }
  };

  const handleInitializeSampleData = () => {
    initializeSampleData();
    setShowDoctorInput(false);
    setIsFirstLaunch(false);
  };

  // Show loading while initializing
  if (!isInitialized || authLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h6">Chargement...</Typography>
      </Container>
    );
  }

  // Show first launch screen
  if (isFirstLaunch) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            🏥 Plateforme Médicale
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Bienvenue dans votre système de gestion médicale
          </Typography>
        </Box>

        <Box sx={{ 
          maxWidth: 600, 
          mx: 'auto', 
          p: 4, 
          border: '1px solid', 
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}>
          <Typography variant="h5" gutterBottom>
            Configuration initiale
          </Typography>
          
          <Typography variant="body1" paragraph>
            Pour commencer, veuillez entrer votre nom de médecin. Ce nom sera utilisé 
            pour identifier tous vos enregistrements dans le système.
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setShowDoctorInput(true)}
              sx={{ mr: 2 }}
            >
              Entrer mon nom
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={handleInitializeSampleData}
            >
              Utiliser des données d'exemple
            </Button>
          </Box>
        </Box>

        <DoctorNameInput 
          open={showDoctorInput} 
          onClose={handleDoctorNameSet}
        />
      </Container>
    );
  }

  // Show normal app
  return (
    <>
      {children}
      <DoctorNameInput 
        open={showDoctorInput} 
        onClose={handleDoctorNameSet}
      />
    </>
  );
};

export default AppInitializer;
